import React, { memo, useMemo } from 'react';
import useIsMobile from './useIsMobile';

/**
 * Блок с контролами «С порубкой».
 *
 * Управляется извне: все значения и обработчики приходят через props.
 */
function CutBlock({
  /* значения ---------------------------------------------------------------- */
  cutPiecesStrParent,
  cutMetersStrParent,
  cutTonsStrParent,

  /* цены -------------------------------------------------------------------- */
  childSheetCost,
  childCutsCost,

  /* изменение текста -------------------------------------------------------- */
  onPiecesChange,
  onMetersChange,
  onTonsChange,

  /* +/- --------------------------------------------------------------------- */
  onMinusPiece,
  onPlusPiece,
  onMinusMeter,
  onPlusMeter,
  onMinusTonn,
  onPlusTonn,

  /* blur / focus ------------------------------------------------------------ */
  onPiecesBlur,
  onMetersBlur,
  onTonsBlur,
  setManualEdit,
  stdSheetCost,
    saleUnit = 1,
  mode,
  onAddToCart,
  stdSliceAnchorRef,
  thickness = 0,    
  canBend = true,
  bendCostTotal = 0,
  stdCutCost = 0,
}) {
  /* ---------- адаптация под ширину экрана ---------- */
  const isMobile = useIsMobile(480);
  // Стоимость товара: либо из дочернего блока, либо (пока он не смонтирован) stdSheetCost
  const sheetCost = React.useMemo(
    () => {
      const fromChild = parseFloat(childSheetCost) || 0;
      const fallback  = parseFloat(stdSheetCost) || 0;
      return fromChild > 0 ? fromChild : fallback;
    },
    [childSheetCost, stdSheetCost]
  );
  // Итог: товар + услуги
  const bendCost = mode === 'bend' ? (parseFloat(bendCostTotal) || 0) : 0;
  // услуги рубки: сразу показываем моментальную оценку, пока визуальный блок не пересчитал
  const cutsCost = React.useMemo(() => {
    const fromChild = parseFloat(childCutsCost) || 0;
    const fallback  = parseFloat(stdCutCost)   || 0;
    return fromChild > 0 ? fromChild : fallback;
  }, [childCutsCost, stdCutCost]);
  const sumCut = React.useMemo(
    () => sheetCost + cutsCost + bendCost,
    [sheetCost, cutsCost, bendCost]
  );
  // Нормализация строк в число: '1,5' -> 1.5, пусто/NaN -> 0
  const toNum = (s) => {
    if (s == null) return 0;
    const n = parseFloat(String(s).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  // Показываем блок «Порубить весь лист по:» только если есть положительное значение
  const hasPositiveValue = React.useMemo(() => {
    const pcs = toNum(cutPiecesStrParent);
    const m   = toNum(cutMetersStrParent);
    const t   = toNum(cutTonsStrParent);
    return pcs > 0 || m > 0 || t > 0;
  }, [cutPiecesStrParent, cutMetersStrParent, cutTonsStrParent]);

  /* ---------- стили (мемоизированы) -------------- */
  const S = useMemo(() => {
    const ORANGE = '#E29E3B';
    const BLUE   = '#2A3486';

    const pmBtnBase = {
      width     : isMobile ? 42 : 25,
      height    : isMobile ? 42 : 25,
      fontSize  : isMobile ? 22 : 14,
      display   : 'flex',
      alignItems: 'center',
      justifyContent:'center',
      border    : `1px solid ${ORANGE}`,
      lineHeight: 1,
      padding   : 0,
      cursor    : 'pointer',
      transition: 'background .15s,color .15s,border .15s',
    };

    return {
      title: {
        margin     : isMobile ? '18px 0 8px' : '14px 0 6px',
        fontSize   : isMobile ? 18 : 15,
        fontWeight : 400,
        color      : BLUE,
        textAlign  : isMobile ? 'center' : 'left',
      },
      row: {
        display        : 'flex',
        flexWrap       : 'wrap',
        gap            : isMobile ? 14 : 12,
        justifyContent : isMobile ? 'center' : 'flex-start',
        marginBottom   : isMobile ? 18 : 12,
      },
      group: {
        display      : 'flex',
        alignItems   : 'center',
        background   : '#f5f7fa',
        padding      : isMobile ? '8px 12px' : '6px 10px',
        boxSizing    : 'border-box',
        ...(isMobile && { flex: '1 1 100%' }),
      },
      btn  : pmBtnBase,
      input: {
        width      : isMobile ? '100%' : 60,
        minWidth   : isMobile ? 0 : 60,
        flex       : isMobile ? 1 : undefined,
        textAlign  : 'center',
        border     : 'none',
        outline    : 'none',
        fontSize   : isMobile ? 18 : 14,
        padding    : isMobile ? '8px 10px' : 0,
        background : 'transparent',
      },
      unit: {
        margin     : isMobile ? '0 8px' : '0 6px',
        fontSize   : isMobile ? 16 : 14,
        whiteSpace : 'nowrap',
      },
      sumBadge: {
        display      : 'flex',
        alignItems   : 'center',
        gap          : 8,
        background   : '#f5f7fa',
        padding      : isMobile ? '10px 14px' : '6px 15px',
        fontSize     : isMobile ? 16 : 14,
        color        : BLUE,
        ...(isMobile
          ? { flex: '1 1 100%', justifyContent: 'center' }
          : { marginLeft: 0 }),
      },
      cartBtn: {
        padding: isMobile ? '14px 0' : '13px 24px',
        width: isMobile ? '100%' : 'auto',
        background: ORANGE,
        border: 'none',
        color: '#fff',
        fontSize: isMobile ? 18 : 14,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background .15s',
      }
    };
  }, [isMobile]);

  /* ---------- helper для одной группы ---------- */
 const renderGroup = (
   { id, value, unit, onChange, onBlur, onMinus, onPlus }
 ) => (
   <div style={S.group}>
      <button type="button" className="pm-btn" style={S.btn} onClick={onMinus}>–</button>
      <input
        id={id}
        style={S.input}
        value={value}
        onChange={e => {
          setManualEdit?.(true);
          onChange(e.target.value);
        }}
        onBlur={() => {
          setManualEdit?.(false);
          onBlur?.();
        }}
        onClick={e => e.target.select()}
      />
      <span style={S.unit}>{unit}</span>
      <button type="button" className="pm-btn" style={S.btn} onClick={onPlus}>+</button>
    </div>
  );
  /* ---------- JSX ---------- */
  return (
    <>
      {/* лёгкий hover у +/- кнопок */}
      <style>{`
        .pm-btn{
          background:none;
          border:1px solid #E29E3B;
          color:#E29E3B;
          -webkit-appearance:none;
          transition:background-color .15s ease,color .15s ease;
          cursor:pointer;
          font-size: 19px;
        }
        .pm-btn:hover{
          background:#E29E3B;
          color:#fff;
        }
        /* tooltip над суммой — аккуратный, со стрелкой */
        .tooltip-wrapper{ position:relative; display:inline-block; }
        .tooltip-trigger{ font-weight:600; }
        .tooltip-text{
          visibility:hidden; opacity:0;
          position:absolute;
          bottom:calc(100% + 10px); left:50%; transform:translateX(-50%);
          background:#ffffff; color:#2b2b2b; border:1px solid #dcdfe3;
          border-radius:10px;
          box-shadow:0 10px 24px rgba(22,27,45,.12);
          font-size:13px; line-height:1.35;
          padding:10px 12px;
          width:max-content; max-width:320px; text-align:left; white-space:normal;
          z-index:50; transition:opacity .18s ease, visibility .18s ease, transform .18s ease;
        }
        .tooltip-text::before, .tooltip-text::after{
          content:""; position:absolute; left:50%; transform:translateX(-50%);
          width:0; height:0; border-style:solid;
        }
        /* стрелка-рамка (светло-серая) */
        .tooltip-text::before{
          top:100%;
          border-width:8px 8px 0 8px;
          border-color:#dcdfe3 transparent transparent transparent;
        }
        /* стрелка-заливка (белая), чуть выше рамки */
        .tooltip-text::after{
          top:calc(100% - 1px);
          border-width:7px 7px 0 7px;
          border-color:#ffffff transparent transparent transparent;
        }
        .tooltip-wrapper:hover .tooltip-text,
        .tooltip-wrapper:focus-within .tooltip-text{
          visibility:visible; opacity:1; transform:translateX(-50%) translateY(-2px);
        }
        .tip-title{
          font-weight:700; color:#2a3486; margin:0 0 6px 0; font-size:13px;
        }
        .tip-grid{
          display:grid; grid-template-columns:1fr auto; gap:4px 12px; align-items:center;
        }
        .tip-grid b{ color:#2a3486; }
        .sum-figure{ font-variant-numeric:tabular-nums; }
      `}</style>
      {/* предупреждение для газовой порезки: шов ≈ 5 мм */}
      {Number(thickness) > 10 && (
        <div style={{ color: '#cc2626', fontSize: 16, margin: '8px 0 10px' }}>
          Внимание: порезка газом — ширина шва ≈ 5&nbsp;мм.
          В расчётах к габаритам каждой детали заложено +5&nbsp;мм.
        </div>
      )}

       <h4 style={S.title}>
        {!canBend
          ? 'С порубкой'
          : (Number(saleUnit) !== 1
              ? 'С порубкой/загибкой/часть листа'
              : 'С порубкой/загибкой')}
      </h4>

      <div style={S.row}>
        {renderGroup({
          id: 'cp_c-pcs',
          value : cutPiecesStrParent,
          unit  : 'шт.',
          onChange: onPiecesChange,
          onBlur : onPiecesBlur,
          onMinus: onMinusPiece,
          onPlus : onPlusPiece,
        })}

        {renderGroup({
          id: 'cp_c-m',
          value : cutMetersStrParent,
          unit  : 'м.',
          onChange: onMetersChange,
          onBlur : onMetersBlur,
          onMinus: onMinusMeter,
          onPlus : onPlusMeter,
        })}

        {renderGroup({
          id: 'cp_c-t',
          value : cutTonsStrParent,
          unit  : 'тн.',
          onChange: onTonsChange,
          onBlur : onTonsBlur,
          onMinus: onMinusTonn,
          onPlus : onPlusTonn,
        })}

 <div style={S.sumBadge}>
   <span className="tooltip-wrapper" tabIndex="0" aria-label="Разбор суммы">
     <span className="tooltip-trigger">Сумма:&nbsp;{sumCut.toFixed(2)}&nbsp;₸</span>
     <span className="tooltip-text" role="tooltip">
       <div className="tip-title">Разбор суммы</div>
       <div className="tip-grid">
         <span>Стоимость листов</span>
         <span className="sum-figure">{sheetCost.toFixed(2)} ₸</span>
         <span>Стоимость рубов</span>
         <span className="sum-figure">{cutsCost.toFixed(2)} ₸</span>
           {mode === 'bend' && bendCost > 0 && (
   <>
     <span>Стоимость загиба</span>
     <span className="sum-figure">{bendCost.toFixed(2)} ₸</span>
   </>
 )}
         <span><b>Итого</b></span>
         <span className="sum-figure"><b>{sumCut.toFixed(2)} ₸</b></span>
       </div>
     </span>
   </span>
 </div>
                <button
          style={{ ...S.cartBtn, opacity: hasPositiveValue ? 1 : .5, cursor: hasPositiveValue ? 'pointer' : 'not-allowed' }}
          onClick={onAddToCart}
          disabled={!hasPositiveValue}
          aria-disabled={!hasPositiveValue}
          title={hasPositiveValue ? 'Добавить в корзину' : 'Укажите количество для порубки'}
        >
          Добавить в корзину
        </button>
      </div>
    </>
  );
}

export default memo(CutBlock);
